import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { usePhaseContext } from '../ConfigurePhase/PhaseAccordion'
import { FieldType } from '../PhaseAttributes/AttributeColsMod'
import {
  DBtoUI,
  OBJtoDB,
  OBJtoUI,
  UItoDB,
  validateFormula,
} from './FormulaFunctions'
import { Examples } from './Examples/FormulaExamples'
import { useStudyAttr } from '../StudyFns'
import { useStudyContext } from '../../../Contexts/Modules/StudyModule/SelectedStudyModule'

function FormulaModal({
  open,
  setClose,
  setCommit,
  data,
  field,
}: {
  open: boolean
  setClose: any
  setCommit: any
  data: any
  field: FieldType
}) {
  const { studyQuery } = useStudyContext()
  const phase = usePhaseContext()
  const histAttr = useStudyAttr()

  const newAttr = useMemo(() => {
    if (phase.attributes) {
      return phase.attributes.filter((a) => a.AttrID !== data.AttrID)
    } else {
      return []
    }
  }, [phase, data])

  const [uiToDBmap, setUItoDBmap] = useState({})
  const [dbToUImap, setDBtoUImap] = useState({})

  const idToName = useMemo(() => {
    const idMap: any = {}
    studyQuery.data?.phases?.forEach((p) => (idMap[p.PhaseID] = p.PhaseName))
    return idMap
  }, [studyQuery.data?.phases])

  const formulaType = data?.AttrDatatype

  //setting two way translation dictionary
  useEffect(() => {
    const uiToDBmap: any = {}
    const dbToUImap: any = {}
    newAttr.forEach((obj) => {
      uiToDBmap[OBJtoUI(obj, idToName, 'new')] = OBJtoDB(obj, 'new')
      dbToUImap[OBJtoDB(obj, 'new')] = OBJtoUI(obj, idToName, 'new')
    })
    histAttr.forEach((obj) => {
      uiToDBmap[OBJtoUI(obj, idToName, 'hist')] = OBJtoDB(obj, 'hist')
      dbToUImap[OBJtoDB(obj, 'hist')] = OBJtoUI(obj, idToName, 'hist')
    })

    setUItoDBmap(uiToDBmap)
    setDBtoUImap(dbToUImap)
  }, [newAttr, histAttr, idToName])

  const textareaRef = useRef<HTMLHeadingElement>(null)

  //unable to capture keyboard shortcuts for select all
  const [cursorDown, setCursorDown] = useState([0, 0]) //click down to select
  const [cursorUp, setCursorUp] = useState<number[] | undefined>([0, 0]) //release click after select

  const [cursorPosition, setCursorPosition] = useState([1, 1]) //[start, end]

  const [uiValue, setUIValue] = useState('=')
  const [dbValue, setDBValue] = useState('=')
  const [error, setError] = useState<null | string>(null)
  const [reset, setReset] = useState(true)

  useEffect(() => {
    const dbVal = data?.[field.field]
    if (dbVal && reset && Object.keys(dbToUImap).length > 0) {
      setDBValue(dbVal)
      setUIValue(DBtoUI(dbVal, dbToUImap))
      setCursorPosition([
        DBtoUI(dbVal, dbToUImap).length,
        DBtoUI(dbVal, dbToUImap).length,
      ])
      setReset(false)
    }
  }, [data, reset, dbToUImap, field])

  type excelFunction = 'AVERAGE' | 'MAX' | 'MIN'
  const [excelFunc, setExcelFunc] = useState<excelFunction | null>(null)

  function insertAttr(value: string, type: 'hist' | 'new') {
    let insertVal
    if (type === 'hist' && excelFunc) {
      insertVal = `${excelFunc}({${value}})`
    } else {
      insertVal = `{${value}}`
    }

    const newUI =
      uiValue.slice(0, cursorPosition[0]) +
      insertVal +
      uiValue.slice(cursorPosition[1])
    setUIValue(newUI)
    setDBValue(UItoDB(newUI, uiToDBmap))
    const error = validateFormula(newUI, UItoDB(newUI, uiToDBmap), formulaType)
    if (error) {
      setError(error)
    } else {
      setError(null)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        setClose()
        setReset(true)
      }}
      sx={{
        '& .MuiPaper-root': {
          maxWidth: 1200,
        },
      }}
    >
      <DialogTitle>Formula Builder</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column' }}
        onPointerLeave={() => {
          //using pointer to avoid incorrect validtion when blur caused by variable click
          setDBValue(UItoDB(uiValue, uiToDBmap))
          const error = validateFormula(
            uiValue,
            UItoDB(uiValue, uiToDBmap),
            formulaType,
          )
          if (error) {
            setError(error)
          }
        }}
      >
        <Alert
          severity={'info'}
          sx={{ width: 1, mb: 1 / 2 }}
        >
          <Typography>
            Use this builder to create a Excel/GoogleSheet formula.
            <br />
            You click an attribute to insert it.
          </Typography>
          <List>
            <ListItem disablePadding>
              <ListItemIcon sx={{ fontSize: 'xx-large', minWidth: 30, my: -5 }}>
                •
              </ListItemIcon>
              <ListItemText>
                <strong>Phase Attribute Value:</strong> A <i>named value</i> for
                an attribute in this Phase
              </ListItemText>
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ fontSize: 'xx-large', minWidth: 30, my: -5 }}>
                •
              </ListItemIcon>
              <ListItemText>
                <strong>Historic Attribute Values:</strong> A <i>named range</i>{' '}
                for an attribute in this Study
              </ListItemText>
            </ListItem>
          </List>
        </Alert>
        <Examples type={formulaType} />
        <Paper
          variant="outlined"
          sx={{ p: 1, my: 1 / 2, display: 'flex', flexWrap: 'wrap' }}
        >
          <Typography
            sx={{ fontWeight: 600, p: 1 }}
            variant={'subtitle2'}
          >
            Phase New Attribute
          </Typography>
          {newAttr.map((attr) => (
            <Chip
              key={attr.AttrID}
              sx={{ m: 1 / 3 }}
              label={OBJtoUI(attr, idToName, 'new')}
              onClick={() => insertAttr(OBJtoUI(attr, idToName, 'new'), 'new')}
            />
          ))}
        </Paper>
        <Paper
          variant="outlined"
          sx={{ p: 1, my: 1 / 2, display: 'flex', flexDirection: 'column' }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', pb: 1 / 2, px: 1 }}>
            <Typography
              sx={{ fontWeight: 600 }}
              variant={'subtitle2'}
            >
              Historic Attribute
            </Typography>
            &nbsp;
            <Typography variant={'body2'}>
              {' '}
              - Returns a list of values.{' '}
            </Typography>
            &nbsp;
            <Typography
              sx={{ display: 'block' }}
              variant={'body2'}
            >
              Should be converted to a value by wrapping in an{' '}
              <strong>excel function</strong> (i.e. buttons on left)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <ButtonGroup
              orientation="vertical"
              sx={{ display: 'flex', m: 1 / 3, mr: 1 }}
            >
              <Button
                color={'inherit'}
                variant={excelFunc === 'MAX' ? 'contained' : undefined}
                disableElevation
                onClick={() =>
                  setExcelFunc((f) => (f === 'MAX' ? null : 'MAX'))
                }
              >
                MAX(...)
              </Button>
              <Button
                color={'inherit'}
                variant={excelFunc === 'MIN' ? 'contained' : undefined}
                disableElevation
                onClick={() =>
                  setExcelFunc((f) => (f === 'MIN' ? null : 'MIN'))
                }
              >
                MIN(...)
              </Button>
              <Button
                color={'inherit'}
                variant={excelFunc === 'AVERAGE' ? 'contained' : undefined}
                disableElevation
                onClick={() =>
                  setExcelFunc((f) => (f === 'AVERAGE' ? null : 'AVERAGE'))
                }
              >
                AVERAGE(...)
              </Button>
            </ButtonGroup>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {histAttr.map((attr) => (
                <Chip
                  key={attr.AttrID}
                  sx={{ m: 1 / 3 }}
                  label={OBJtoUI(attr, idToName, 'hist')}
                  onClick={() =>
                    insertAttr(OBJtoUI(attr, idToName, 'hist'), 'hist')
                  }
                />
              ))}
            </Box>
          </Box>
        </Paper>
        <TextField
          label={'Editor'}
          margin="normal"
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          ref={textareaRef}
          value={uiValue || '='}
          onChange={(e) => {
            setError(null)
            setUIValue(e.target.value)
          }}
          onSelect={(e: any) => {
            if (e.target.selectionStart && e.target.selectionEnd) {
              // console.log('select', [e.target.selectionStart, e.target.selectionEnd])
              setCursorPosition([
                e.target.selectionStart < 1 ? 1 : e.target.selectionStart,
                e.target.selectionEnd,
              ])
            }
          }}
          onPointerDown={(e: any) => {
            //reset pointer up
            setCursorUp(undefined)
            if (e.target.selectionStart && e.target.selectionEnd) {
              // console.log('down', e.target.selectionStart, e.target.selectionEnd)
              setCursorDown([e.target.selectionStart, e.target.selectionEnd])
            }
          }}
          onPointerUp={(e: any) => {
            if (e.target.selectionStart && e.target.selectionEnd) {
              // console.log('up', e.target.selectionStart, e.target.selectionEnd)
              setCursorUp([e.target.selectionStart, e.target.selectionEnd])
            }
          }}
          onPointerLeave={(e: any) => {
            // console.log('leave', e.target.selectionStart, e.target.selectionEnd)
            if (
              e.target.selectionStart === undefined &&
              cursorUp === undefined
            ) {
              setCursorPosition([1, cursorDown[0]])
            } else if (e.target.selectionStart && e.target.selectionEnd) {
              setCursorPosition([
                e.target.selectionStart < 1 ? 1 : e.target.selectionStart,
                e.target.selectionEnd,
              ])
            }
          }}
          error={!!error}
          helperText={error}
          autoFocus
        />
        <TextField
          disabled
          label={'Saved Formula'}
          margin="normal"
          fullWidth
          multiline
          value={dbValue || '='}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color={'error'}
          onClick={() => {
            setClose()
            setReset(true)
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={!setCommit || !!error}
          color={'success'}
          onClick={() => setCommit(dbValue)}
          autoFocus
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FormulaModal
